import type { RouteRecordRaw } from 'vue-router';
import { Admin, Authenticated, ModelBindings, Require2FA } from '~/middlewares';
import { useModelsStore } from '~/stores';

export const adminRoutes: RouteRecordRaw[] = [
    {
        path: '/admin',
        component: () => import('~/views/GenericLayout.vue'),
        meta: {
            middlewares: [Authenticated, Admin, Require2FA, new ModelBindings('admin')],
            showChildrenInNavbar: true,
        },
        children: [
            // Basic Administration
            {
                name: 'admin.administration',
                path: '',
                component: () => import('~/views/Passthrough.vue'),
                meta: {
                    showAsCategory: true,
                },
                children: [
                    {
                        name: 'admin.administration.index',
                        path: '',
                        component: () => import('~/views/admin/Index.vue'),
                        meta: {
                            icon: 'home',
                        },
                    },
                    {
                        name: 'admin.administration.settings',
                        path: 'settings',
                        component: () => import('~/views/layouts/TabsLayout.vue'),
                        redirect: { name: 'admin.administration.settings.general' },
                        meta: {
                            icon: 'cog',
                            passthrough: true,
                            tabRoot: true,
                        },
                        children: [
                            {
                                name: 'admin.administration.settings.general',
                                path: '',
                                component: () => import('~/views/admin/settings/General.vue'),
                                meta: {
                                    icon: 'cog',
                                },
                            },
                            {
                                name: 'admin.administration.settings.branding',
                                path: 'branding',
                                component: () => import('~/views/admin/settings/Branding.vue'),
                                meta: {
                                    icon: 'cog',
                                    feature: 'admin:branding',
                                },
                            },
                            {
                                name: 'admin.administration.settings.embed',
                                path: 'embed',
                                component: () => import('~/views/admin/settings/Embed.vue'),
                                meta: {
                                    icon: 'cog',
                                    feature: 'admin:opengraph',
                                },
                            },
                            {
                                name: 'admin.administration.settings.js_injector',
                                path: 'js',
                                component: () => import('~/views/admin/settings/JSInjector.vue'),
                                meta: {
                                    icon: 'cog',
                                    feature: 'admin:js-injector',
                                },
                            },
                            {
                                name: 'admin.administration.settings.css_injector',
                                path: 'css',
                                component: () => import('~/views/admin/settings/CSSInjector.vue'),
                                meta: {
                                    icon: 'cog',
                                    feature: 'admin:css-injector',
                                },
                            },
                            {
                                name: 'admin.administration.settings.whmcs',
                                path: 'whmcs',
                                component: () => import('~/views/admin/settings/WHMCS.vue'),
                                meta: {
                                    icon: 'cog',
                                    feature: 'admin:whmcs-sso',
                                },
                            },
                            {
                                name: 'admin.administration.settings.smtp',
                                path: 'smtp',
                                component: () => import('~/views/admin/settings/SMTP.vue'),
                                meta: {
                                    icon: 'cog',
                                    feature: 'admin:custom-smtp',
                                },
                            },
                            {
                                name: 'admin.administration.settings.firewall',
                                path: 'firewall',
                                component: () => import('~/views/admin/settings/Firewall.vue'),
                                meta: {
                                    icon: 'cog',
                                    feature: 'admin:firewall',
                                },
                            },
                        ],
                    },
                    {
                        name: 'admin.administration.migrator',
                        path: 'migrator',
                        component: () => import('~/views/Passthrough.vue'),
                        redirect: { name: 'admin.administration.migrator.index' },
                        meta: {
                            icon: 'random',
                            permission: 'admin:migrator.read',
                            passthrough: true,
                        },
                        children: [
                            {
                                name: 'admin.administration.migrator.index',
                                path: '',
                                component: () => import('~/views/admin/migrator/Index.vue'),
                                meta: {
                                    icon: 'random',
                                    permission: 'admin:migrator.read',
                                },
                            },
                            {
                                name: 'admin.administration.migrator.new',
                                path: 'new',
                                component: () => import('~/views/admin/migrator/Wizard.vue'),
                                meta: {
                                    icon: 'random',
                                    permission: 'admin:migrator.update',
                                },
                            },
                            {
                                name: 'admin.administration.migrator.view',
                                path: ':migration',
                                component: () => import('~/views/admin/migrator/Wizard.vue'),
                                meta: {
                                    icon: 'random',
                                    permission: 'admin:migrator.read',
                                },
                            },
                        ],
                    },
                    {
                        name: 'admin.administration.email_templates',
                        path: 'email-templates',
                        component: () => import('~/views/admin/emails/Templates.vue'),
                        meta: {
                            icon: 'envelope',
                            feature: 'admin:email-templates',
                        },
                    },
                    {
                        name: 'admin.administration.announcements',
                        path: 'announcements',
                        component: () => import('~/views/admin/announcements/Index.vue'),
                        meta: {
                            icon: 'bullhorn',
                            permission: 'admin:announcement.read',
                            feature: 'general:announcements',
                        },
                    },
                    {
                        name: 'admin.administration.application_api',
                        path: 'application-api',
                        component: () => import('~/views/Passthrough.vue'),
                        redirect: { name: 'admin.administration.application_api.index' },
                        meta: {
                            icon: 'key',
                            permission: 'admin:application_api.read',
                            feature: 'admin:application-api',
                            passthrough: true,
                        },
                        children: [
                            {
                                name: 'admin.administration.application_api.index',
                                path: '',
                                component: () => import('~/views/admin/application-api/Index.vue'),
                                meta: {
                                    icon: 'key',
                                    permission: 'admin:application_api.read',
                                },
                            },
                            {
                                name: 'admin.administration.application_api.new',
                                path: 'new',
                                component: () => import('~/views/admin/application-api/Manage.vue'),
                                meta: {
                                    icon: 'key',
                                    permission: 'admin:application_api.create',
                                },
                            },
                            {
                                name: 'admin.administration.application_api.manage',
                                path: ':apiKey',
                                component: () => import('~/views/admin/application-api/Manage.vue'),
                                meta: {
                                    icon: 'key',
                                },
                            },
                        ],
                    },
                ],
            },

            // Management
            {
                name: 'admin.management',
                path: '',
                component: () => import('~/views/Passthrough.vue'),
                meta: {
                    showAsCategory: true,
                },
                children: [
                    {
                        name: 'admin.management.database_hosts',
                        path: 'databases',
                        component: () => import('~/views/Passthrough.vue'),
                        redirect: { name: 'admin.management.database_hosts.index' },
                        meta: {
                            icon: 'database',
                            permission: 'admin:database_host.read',
                            passthrough: true,
                        },
                        children: [
                            {
                                name: 'admin.management.database_hosts.index',
                                path: '',
                                component: () => import('~/views/admin/database-hosts/Index.vue'),
                                meta: {
                                    icon: 'database',
                                    permission: 'admin:database_host.read',
                                },
                            },
                            {
                                name: 'admin.management.database_hosts.manage',
                                path: ':databaseHost',
                                component: () => import('~/views/admin/database-hosts/Manage.vue'),
                                meta: {
                                    icon: 'database',
                                },
                            },
                        ],
                    },

                    {
                        name: 'admin.management.tags',
                        path: 'tags',
                        component: () => import('~/views/Passthrough.vue'),
                        redirect: { name: 'admin.management.tags.index' },
                        meta: {
                            icon: 'tag',
                            permission: 'admin:tag.read',
                            passthrough: true,
                        },
                        children: [
                            {
                                name: 'admin.management.tags.index',
                                path: '',
                                component: () => import('~/views/admin/tags/Index.vue'),
                                meta: {
                                    icon: 'tag',
                                    permission: 'admin:tag.read',
                                },
                            },
                            {
                                name: 'admin.management.tags.manage',
                                path: ':tag',
                                component: () => import('~/views/admin/tags/Manage.vue'),
                                meta: {
                                    icon: 'tag',
                                    permission: 'admin:tag.read',
                                },
                            },
                        ],
                    },

                    {
                        name: 'admin.management.locations',
                        path: 'locations',
                        component: () => import('~/views/Passthrough.vue'),
                        redirect: { name: 'admin.management.locations.index' },
                        meta: {
                            icon: 'globe',
                            permission: 'admin:location.read',
                            passthrough: true,
                        },
                        children: [
                            {
                                name: 'admin.management.locations.index',
                                path: '',
                                component: () => import('~/views/admin/locations/Index.vue'),
                                meta: {
                                    icon: 'globe',
                                    permission: 'admin:location.read',
                                },
                            },
                            {
                                name: 'admin.management.locations.manage',
                                path: ':location',
                                component: () => import('~/views/admin/locations/Manage.vue'),
                                meta: {
                                    icon: 'globe',
                                },
                            },
                        ],
                    },

                    {
                        name: 'admin.management.nodes',
                        path: 'nodes',
                        component: () => import('~/views/Passthrough.vue'),
                        redirect: { name: 'admin.management.nodes.index' },
                        meta: {
                            icon: 'network-wired',
                            permission: 'admin:node.read',
                            passthrough: true,
                        },
                        children: [
                            {
                                name: 'admin.management.nodes.index',
                                path: '',
                                component: () => import('~/views/layouts/TabsLayout.vue'),
                                redirect: { name: 'admin.management.nodes.index.list' },
                                meta: {
                                    icon: 'network-wired',
                                    permission: 'admin:node.read',
                                    passthrough: true,
                                    tabRoot: true,
                                },
                                children: [
                                    {
                                        name: 'admin.management.nodes.index.list',
                                        path: '',
                                        component: () => import('~/views/admin/nodes/Index.vue'),
                                        meta: {
                                            icon: 'network-wired',
                                            permission: 'admin:node.read',
                                        },
                                    },
                                    {
                                        name: 'admin.management.nodes.index.settings',
                                        path: 'settings',
                                        component: () => import('~/views/admin/nodes/Settings.vue'),
                                        meta: {
                                            icon: 'cog',
                                            permission: 'admin:settings.update',
                                        },
                                    },
                                ],
                            },
                            {
                                name: 'admin.management.nodes.manage',
                                path: ':node',
                                component: () => import('~/views/layouts/TabsLayout.vue'),
                                redirect: { name: 'admin.management.nodes.manage.about' },
                                meta: { tabRoot: true },
                                children: [
                                    {
                                        name: 'admin.management.nodes.manage.about',
                                        path: '',
                                        component: () => import('~/views/admin/nodes/manage/About.vue'),
                                        meta: {
                                            icon: 'network-wired',
                                            permission: 'admin:node.read',
                                        },
                                    },
                                    {
                                        name: 'admin.management.nodes.manage.settings',
                                        path: 'settings',
                                        component: () => import('~/views/admin/nodes/manage/Settings.vue'),
                                        meta: {
                                            icon: 'wrench',
                                            permission: 'admin:node.read',
                                        },
                                    },
                                    {
                                        name: 'admin.management.nodes.manage.allocations',
                                        path: 'allocations',
                                        component: () => import('~/views/admin/nodes/manage/Allocations.vue'),
                                        meta: {
                                            icon: 'network-wired',
                                            permission: 'admin:node_allocation.read',
                                        },
                                    },
                                    {
                                        name: 'admin.management.nodes.manage.servers',
                                        path: 'servers',
                                        component: () => import('~/views/admin/nodes/manage/Servers.vue'),
                                        meta: {
                                            icon: 'server',
                                            permission: 'admin:server.read',
                                        },
                                    },
                                ],
                            },
                        ],
                    },

                    {
                        name: 'admin.management.servers',
                        path: 'servers',
                        component: () => import('~/views/Passthrough.vue'),
                        redirect: { name: 'admin.management.servers.index' },
                        meta: {
                            icon: 'server',
                            permission: 'admin:server.read',
                            passthrough: true,
                        },
                        children: [
                            {
                                name: 'admin.management.servers.index',
                                path: '',
                                component: () => import('~/views/admin/servers/Index.vue'),
                                meta: {
                                    icon: 'server',
                                    permission: 'admin:server.read',
                                },
                            },
                            {
                                name: 'admin.management.servers.new',
                                path: 'new',
                                component: () => import('~/views/admin/servers/New.vue'),
                                meta: {
                                    icon: 'server',
                                    permission: 'admin:server.create',
                                },
                            },
                            {
                                name: 'admin.management.servers.manage',
                                path: ':server',
                                component: () => import('~/views/admin/servers/manage/ServerManageLayout.vue'),
                                redirect: { name: 'admin.management.servers.manage.about' },
                                meta: { tabRoot: true },
                                children: [
                                    {
                                        name: 'admin.management.servers.manage.about',
                                        path: '',
                                        component: () => import('~/views/admin/servers/manage/About.vue'),
                                        meta: {
                                            icon: 'network-wired',
                                            permission: 'admin:server.read',
                                        },
                                    },
                                    {
                                        name: 'admin.management.servers.manage.details',
                                        path: 'details',
                                        component: () => import('~/views/admin/servers/manage/Details.vue'),
                                        meta: {
                                            icon: 'network-wired',
                                            permission: 'admin:server.read',
                                        },
                                    },
                                    {
                                        name: 'admin.management.servers.manage.template',
                                        path: 'template',
                                        component: () => import('~/views/admin/servers/manage/Template.vue'),
                                        meta: {
                                            icon: 'clone',
                                            permission: 'admin:server_template.read',
                                        },
                                    },
                                    {
                                        name: 'admin.management.servers.manage.build_configuration',
                                        path: 'build',
                                        component: () => import('~/views/admin/servers/manage/BuildConfiguration.vue'),
                                        meta: {
                                            icon: 'network-wired',
                                            permission: 'admin:server.read',
                                        },
                                    },
                                    {
                                        name: 'admin.management.servers.manage.startup',
                                        path: 'startup',
                                        component: () => import('~/views/admin/servers/manage/Startup.vue'),
                                        meta: {
                                            icon: 'network-wired',
                                            permission: 'admin:server_startup.read',
                                        },
                                    },
                                    {
                                        name: 'admin.management.servers.manage.databases',
                                        path: 'databases',
                                        component: () => import('~/views/admin/servers/manage/Databases.vue'),
                                        meta: {
                                            icon: 'network-wired',
                                            permission: 'admin:server_database.read',
                                        },
                                    },
                                    {
                                        name: 'admin.management.servers.manage.firewall',
                                        path: 'firewall',
                                        component: () => import('~/views/admin/servers/manage/Firewall.vue'),
                                        meta: {
                                            icon: 'shield-alt',
                                            feature: 'admin:firewall',
                                            permission: 'admin:server.sync_firewall',
                                            requiresFirewallProviders: true,
                                        },
                                    },
                                    {
                                        name: 'admin.management.servers.manage.advanced',
                                        path: 'advanced',
                                        component: () => import('~/views/admin/servers/manage/Advanced.vue'),
                                        meta: {
                                            icon: 'network-wired',
                                            permission: 'admin:server.read',
                                        },
                                    },
                                    {
                                        name: 'admin.management.servers.manage.go_to_server',
                                        path: 'go_to_server', // TODO: rename this to something better?
                                        component: () => import('~/views/Passthrough.vue'),
                                        beforeEnter: () => {
                                            const modelsStore = useModelsStore();
                                            return {
                                                name: 'server.system.index',
                                                params: { server: modelsStore.server?.getSluggedRouteID() },
                                            };
                                        },
                                        meta: {
                                            adminOnly: true,
                                            icon: 'external-link-alt',
                                        },
                                    },
                                ],
                            },

                            {
                                // Backwards compatibility with WHMCS's go go Service button
                                name: 'admin.management.servers.view',
                                path: 'view/:server',
                                meta: {
                                    hidden: true,
                                },
                                redirect: (to) => {
                                    return {
                                        name: 'admin.management.servers.manage.about',
                                        params: {
                                            server: to.params.server,
                                        },
                                    };
                                },
                            },
                        ],
                    },

                    {
                        name: 'admin.management.metrics',
                        path: 'metrics',
                        component: () => import('~/views/layouts/TabsLayout.vue'),
                        redirect: { name: 'admin.management.metrics.index' },
                        meta: {
                            icon: 'chart-line',
                            permission: 'admin:metrics.read',
                            feature: 'admin:metrics',
                            passthrough: true,
                            tabRoot: true,
                        },
                        children: [
                            {
                                name: 'admin.management.metrics.index',
                                path: '',
                                component: () => import('~/views/admin/metrics/Overview.vue'),
                                meta: {
                                    icon: 'chart-line',
                                    permission: 'admin:metrics.read',
                                },
                            },
                            {
                                name: 'admin.management.metrics.nodes',
                                path: 'nodes',
                                component: () => import('~/views/admin/metrics/Breakdown.vue'),
                                props: { dimension: 'node' },
                                meta: {
                                    icon: 'server',
                                    permission: 'admin:metrics.read',
                                },
                            },
                            {
                                name: 'admin.management.metrics.eggs',
                                path: 'eggs',
                                component: () => import('~/views/admin/metrics/Breakdown.vue'),
                                props: { dimension: 'egg' },
                                meta: {
                                    icon: 'egg',
                                    permission: 'admin:metrics.read',
                                },
                            },
                            {
                                name: 'admin.management.metrics.locations',
                                path: 'locations',
                                component: () => import('~/views/admin/metrics/Breakdown.vue'),
                                props: { dimension: 'location' },
                                meta: {
                                    icon: 'globe',
                                    permission: 'admin:metrics.read',
                                },
                            },
                            {
                                name: 'admin.management.metrics.egg_tags',
                                path: 'egg-tags',
                                component: () => import('~/views/admin/metrics/Breakdown.vue'),
                                props: { dimension: 'egg_tag' },
                                meta: {
                                    icon: 'tag',
                                    permission: 'admin:metrics.read',
                                },
                            },
                            {
                                name: 'admin.management.metrics.deployment_tags',
                                path: 'deployment-tags',
                                component: () => import('~/views/admin/metrics/Breakdown.vue'),
                                props: { dimension: 'deployment_tag' },
                                meta: {
                                    icon: 'tag',
                                    permission: 'admin:metrics.read',
                                },
                            },
                        ],
                    },

                    {
                        name: 'admin.management.users',
                        path: 'users',
                        component: () => import('~/views/Passthrough.vue'),
                        redirect: { name: 'admin.management.users.index' },
                        meta: {
                            icon: 'users',
                            permission: 'admin:user.read',
                            passthrough: true,
                        },
                        children: [
                            {
                                name: 'admin.management.users.index',
                                path: '',
                                component: () => import('~/views/admin/users/Index.vue'),
                                meta: {
                                    icon: 'users',
                                    permission: 'admin:user.read',
                                },
                            },

                            {
                                name: 'admin.management.users.new',
                                path: 'new',
                                component: () => import('~/views/admin/users/manage/About.vue'),
                                meta: {
                                    icon: 'user',
                                    permission: 'admin:user.create',
                                },
                            },
                            {
                                name: 'admin.management.users.manage',
                                path: ':user',
                                component: () => import('~/views/layouts/TabsLayout.vue'),
                                redirect: { name: 'admin.management.users.manage.about' },
                                meta: { tabRoot: true },
                                children: [
                                    {
                                        name: 'admin.management.users.manage.about',
                                        path: '',
                                        component: () => import('~/views/admin/users/manage/About.vue'),
                                        meta: {
                                            icon: 'user',
                                        },
                                    },
                                    {
                                        name: 'admin.management.users.manage.servers',
                                        path: 'servers',
                                        component: () => import('~/views/admin/users/manage/Servers.vue'),
                                        meta: {
                                            icon: 'server',
                                            permission: 'admin:server.read',
                                        },
                                    },
                                    {
                                        name: 'admin.management.users.manage.audit_logs',
                                        path: 'audit-logs',
                                        component: () => import('~/views/admin/users/manage/AuditLogs.vue'),
                                        meta: {
                                            icon: 'list',
                                            permission: 'admin:user_audit.read',
                                        },
                                    },
                                    {
                                        name: 'admin.management.users.manage.emails',
                                        path: 'emails',
                                        component: () => import('~/views/admin/users/manage/Emails.vue'),
                                        meta: {
                                            icon: 'envelope',
                                            permission: 'admin:email.read',
                                        },
                                    },
                                ],
                            },
                        ],
                    },

                    {
                        name: 'admin.management.emails',
                        path: 'emails',
                        component: () => import('~/views/Passthrough.vue'),
                        redirect: { name: 'admin.management.emails.index' },
                        meta: {
                            icon: 'envelope',
                            permission: 'admin:email.read',
                            passthrough: true,
                        },
                        children: [
                            {
                                name: 'admin.management.emails.index',
                                path: '',
                                component: () => import('~/views/admin/emails/Index.vue'),
                                meta: {
                                    icon: 'envelope',
                                    permission: 'admin:email.read',
                                },
                            },
                            {
                                name: 'admin.management.emails.view',
                                path: ':sentEmail',
                                component: () => import('~/views/admin/emails/View.vue'),
                                meta: {
                                    icon: 'envelope',
                                    permission: 'admin:email.read',
                                    hidden: true,
                                },
                            },
                        ],
                    },
                ],
            },

            {
                name: 'admin.service_management',
                path: '',
                component: () => import('~/views/Passthrough.vue'),
                meta: {
                    showAsCategory: true,
                },
                children: [
                    {
                        name: 'admin.service_management.eggs',
                        path: 'eggs',
                        component: () => import('~/views/Passthrough.vue'),
                        redirect: { name: 'admin.service_management.eggs.index' },
                        meta: {
                            icon: 'egg',
                            permission: 'admin:egg.read',
                            passthrough: true,
                        },
                        children: [
                            {
                                name: 'admin.service_management.eggs.index',
                                path: '',
                                component: () => import('~/views/admin/eggs/Index.vue'),
                                meta: {
                                    icon: 'egg',
                                    permission: 'admin:egg.read',
                                },
                            },

                            {
                                name: 'admin.service_management.eggs.new',
                                path: 'new',
                                component: () => import('~/views/admin/nests/eggs/Configuration.vue'),
                                meta: {
                                    icon: 'egg',
                                    permission: 'admin:egg.create',
                                },
                            },

                            {
                                name: 'admin.service_management.eggs.show',
                                path: ':egg',
                                component: () => import('~/views/layouts/TabsLayout.vue'),
                                redirect: { name: 'admin.service_management.eggs.configuration' },
                                meta: { tabRoot: true },
                                children: [
                                    {
                                        name: 'admin.service_management.eggs.configuration',
                                        path: '',
                                        component: () => import('~/views/admin/nests/eggs/Configuration.vue'),
                                        meta: {
                                            icon: 'egg',
                                            permission: 'admin:egg.read',
                                        },
                                    },

                                    {
                                        name: 'admin.service_management.eggs.variables',
                                        path: 'variables',
                                        component: () => import('~/views/admin/nests/eggs/Variables.vue'),
                                        meta: {
                                            icon: 'egg',
                                            permission: 'admin:egg_variable.read',
                                        },
                                    },

                                    {
                                        name: 'admin.service_management.eggs.scripts',
                                        path: 'scripts',
                                        component: () => import('~/views/admin/nests/eggs/Scripts.vue'),
                                        meta: {
                                            icon: 'egg',
                                            permission: 'admin:egg.read',
                                        },
                                    },

                                    {
                                        name: 'admin.service_management.eggs.thumbnail',
                                        path: 'thumbnail',
                                        component: () => import('~/views/admin/nests/eggs/Thumbnail.vue'),
                                        meta: {
                                            icon: 'egg',
                                            permission: 'admin:egg.read',
                                        },
                                    },

                                    {
                                        name: 'admin.service_management.eggs.catalog_export',
                                        path: 'catalog-export',
                                        component: () => import('~/views/admin/nests/eggs/CatalogExport.vue'),
                                        meta: {
                                            icon: 'egg',
                                            permission: 'admin:egg.read',
                                            requiresCanExport: true,
                                        },
                                    },

                                    {
                                        name: 'admin.service_management.eggs.config_editor',
                                        path: 'config-editor',
                                        component: () => import('~/views/admin/nests/eggs/ConfigEditorConfiguration.vue'),
                                        meta: {
                                            icon: 'egg',
                                            permission: 'admin:egg.read',
                                            // The feature is physgun-gated backend-side, which already
                                            // makes it unpurchasable and therefore hidden rather than
                                            // locked. Naming the addon states that here instead of
                                            // leaving it implied by the entitlement payload.
                                            feature: 'admin:config-editor',
                                            addon: 'physgun',
                                        },
                                    },

                                    {
                                        name: 'admin.service_management.eggs.firewall',
                                        path: 'firewall',
                                        component: () => import('~/views/admin/nests/eggs/FirewallConfiguration.vue'),
                                        meta: {
                                            icon: 'egg',
                                            permission: 'admin:egg.read',
                                            feature: 'admin:firewall',
                                            requiresFirewallProviders: true,
                                        },
                                    },

                                    {
                                        name: 'admin.service_management.eggs.switch',
                                        path: 'switch',
                                        component: () => import('~/views/admin/nests/eggs/SwitchConfiguration.vue'),
                                        meta: {
                                            icon: 'egg',
                                            permission: 'admin:egg.read',
                                        },
                                    },

                                    {
                                        name: 'admin.service_management.eggs.hytale_configuration',
                                        path: 'hytale-configuration',
                                        component: () => import('~/views/admin/nests/eggs/HytaleConfiguration.vue'),
                                        meta: {
                                            icon: 'egg',
                                            permission: 'admin:settings.update',
                                            tags: 'hytale',
                                        },
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        name: 'admin.service_management.egg_catalog',
                        path: 'egg-catalog',
                        component: () => import('~/views/admin/eggs/Catalog.vue'),
                        meta: {
                            icon: 'cloud-download-alt',
                            permission: 'admin:egg.read',
                        },
                    },

                    {
                        name: 'admin.service_management.egg_switching',
                        path: 'egg-switching',
                        component: () => import('~/views/layouts/TabsLayout.vue'),
                        redirect: { name: 'admin.service_management.egg_switching.general' },
                        meta: {
                            icon: 'random',
                            permission: 'admin:switch_group.read',
                            tabRoot: true,
                        },
                        children: [
                            {
                                name: 'admin.service_management.egg_switching.general',
                                path: '',
                                component: () => import('~/views/admin/egg-switching/General.vue'),
                                meta: {
                                    icon: 'random',
                                    permission: 'admin:switch_group.read',
                                },
                            },
                            {
                                name: 'admin.service_management.egg_switching.switch_groups',
                                path: 'groups',
                                component: () => import('~/views/admin/egg-switching/Index.vue'),
                                meta: {
                                    icon: 'random',
                                    permission: 'admin:switch_group.read',
                                    // Fill the viewport so the board columns scroll
                                    // internally instead of growing the whole page.
                                    stretch: true,
                                },
                            },
                        ],
                    },

                    {
                        name: 'admin.management.server_templates',
                        path: 'templates',
                        component: () => import('~/views/Passthrough.vue'),
                        redirect: { name: 'admin.management.server_templates.index' },
                        meta: {
                            icon: 'clone',
                            permission: 'admin:server_template.read',
                            passthrough: true,
                        },
                        children: [
                            {
                                name: 'admin.management.server_templates.index',
                                path: '',
                                component: () => import('~/views/admin/server-templates/Index.vue'),
                                meta: {
                                    icon: 'clone',
                                    permission: 'admin:server_template.read',
                                },
                            },
                            {
                                name: 'admin.management.server_templates.new',
                                path: 'new',
                                component: () => import('~/views/admin/server-templates/New.vue'),
                                meta: {
                                    icon: 'clone',
                                    permission: 'admin:server_template.create',
                                },
                            },
                            {
                                name: 'admin.management.server_templates.import',
                                path: 'import',
                                component: () => import('~/views/admin/server-templates/Import.vue'),
                                meta: {
                                    icon: 'cloud-arrow-down',
                                    permission: 'admin:server_template.create',
                                },
                            },
                            {
                                name: 'admin.management.server_templates.compare',
                                path: 'compare',
                                component: () => import('~/views/admin/server-templates/Compare.vue'),
                                meta: {
                                    icon: 'code-compare',
                                    permission: 'admin:server_template.read',
                                },
                            },
                            {
                                name: 'admin.management.server_templates.manage',
                                path: ':serverTemplate',
                                component: () => import('~/views/layouts/TabsLayout.vue'),
                                redirect: { name: 'admin.management.server_templates.manage.configuration' },
                                meta: { tabRoot: true },
                                children: [
                                    {
                                        name: 'admin.management.server_templates.manage.configuration',
                                        path: '',
                                        component: () => import('~/views/admin/server-templates/manage/Configuration.vue'),
                                        meta: {
                                            icon: 'clone',
                                            permission: 'admin:server_template.read',
                                        },
                                    },
                                    {
                                        name: 'admin.management.server_templates.manage.schedules',
                                        path: 'schedules',
                                        component: () => import('~/views/admin/server-templates/manage/Schedules.vue'),
                                        meta: {
                                            icon: 'calendar-alt',
                                            permission: 'admin:server_template.read',
                                        },
                                    },
                                    {
                                        name: 'admin.management.server_templates.manage.servers',
                                        path: 'servers',
                                        component: () => import('~/views/admin/server-templates/manage/Servers.vue'),
                                        meta: {
                                            icon: 'server',
                                            permission: 'admin:server.read',
                                        },
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        name: 'admin.service_management.mods',
                        path: 'mods',
                        component: () => import('~/views/layouts/TabsLayout.vue'),
                        redirect: { name: 'admin.service_management.mods.index' },
                        meta: {
                            icon: 'cubes',
                            passthrough: true,
                            tabRoot: true,
                        },
                        children: [
                            {
                                name: 'admin.service_management.mods.index',
                                path: '',
                                component: () => import('~/views/admin/mods/Index.vue'),
                                meta: {
                                    icon: 'cubes',
                                    // permission: 'admin:mod.read',
                                },
                            },
                            {
                                name: 'admin.service_management.mods.new',
                                path: 'new',
                                component: () => import('~/views/admin/mods/Manage.vue'),
                                meta: {
                                    icon: 'cube',
                                    tab: false,
                                },
                            },
                            {
                                name: 'admin.service_management.mods.manage',
                                path: ':mod',
                                component: () => import('~/views/admin/mods/Manage.vue'),
                                meta: {
                                    icon: 'code-branch',
                                    tab: false,
                                },
                            },
                            {
                                name: 'admin.service_management.mods.modpacks',
                                path: 'modpacks',
                                component: () => import('~/views/admin/mods/modpacks/Index.vue'),
                                meta: {
                                    icon: 'boxes',
                                    permission: 'admin:modpack.read',
                                },
                            },
                            {
                                name: 'admin.service_management.mods.modpacks_manage',
                                path: 'modpacks/:platform/:modpackId',
                                component: () => import('~/views/admin/mods/modpacks/manage/Index.vue'),
                                meta: {
                                    icon: 'boxes',
                                    tab: false,
                                    permission: 'admin:modpack.read',
                                },
                            },
                            {
                                name: 'admin.service_management.mods.modpacks_logs',
                                path: 'modpacks/:platform/:modpackId/:versionId/logs',
                                component: () => import('~/views/admin/mods/modpacks/manage/BuildLogs.vue'),
                                meta: {
                                    icon: 'file-lines',
                                    tab: false,
                                    permission: 'admin:modpack.read',
                                },
                            },
                        ],
                    },
                    {
                        name: 'admin.service_management.domains',
                        path: 'domains',
                        component: () => import('~/views/Passthrough.vue'),
                        redirect: { name: 'admin.service_management.domains.index' },
                        meta: {
                            icon: 'globe',
                            permission: 'admin:domain.read',
                            passthrough: true,
                        },
                        children: [
                            {
                                name: 'admin.service_management.domains.index',
                                path: '',
                                component: () => import('~/views/admin/domains/Index.vue'),
                                meta: {
                                    icon: 'globe',
                                    permission: 'admin:domain.read',
                                },
                            },
                            {
                                name: 'admin.service_management.domains.new',
                                path: 'new',
                                component: () => import('~/views/admin/domains/manage/Configuration.vue'),
                                meta: {
                                    icon: 'globe',
                                    permission: 'admin:domain.create',
                                },
                            },
                            {
                                name: 'admin.service_management.domains.manage',
                                path: ':domain',
                                component: () => import('~/views/layouts/TabsLayout.vue'),
                                redirect: { name: 'admin.service_management.domains.manage.configuration' },
                                meta: { tabRoot: true },
                                children: [
                                    {
                                        name: 'admin.service_management.domains.manage.configuration',
                                        path: '',
                                        component: () => import('~/views/admin/domains/manage/Configuration.vue'),
                                        meta: {
                                            icon: 'globe',
                                            permission: 'admin:domain.read',
                                        },
                                    },
                                    {
                                        name: 'admin.service_management.domains.manage.servers',
                                        path: 'servers',
                                        component: () => import('~/views/admin/domains/manage/Servers.vue'),
                                        meta: {
                                            icon: 'globe',
                                            permission: 'admin:server.read',
                                        },
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
        ],
    },
];
